import { create } from "zustand";
import { getChat, ChatV2response, chatApi, chatApiStream, FeedbackV2, sendFeedbackApi, sendEscalationMessageApi, EscalationMessage } from "../../api";
import { ApiError } from "../../api/controller";
import { ChatStore, ChatItem } from "../types";
import { useSecuritySetting, persistStore, useMessageStore, useHistoryStore, useSpeechStore, useConfigStore, usePersonalizationStore } from "../store";

// Helper function to prepare initial chat state with user question
const prepareInitialChatState = (question: string, currentChats: ChatItem[], hasActiveChat: boolean): ChatItem[] => {
    const newAnswer: [string, ChatV2response] = [
        question,
        { answer: "", chat_id: "", row_key: "", start_new_chat: false }
    ];

    if (!hasActiveChat) {
        return [
            ...currentChats.map((chat) => ({ ...chat, active: false })),
            {
                chat_id: "",
                loading: true,
                answers: [newAnswer],
                active: true,
                moveTo: "",
                error: "",
                startNewChat: false,
                updateMessage: undefined,
                streamingChunks: [],
            }
        ];
    }

    return currentChats.map((chat) =>
        chat.active
            ? { ...chat, answers: [...chat.answers, newAnswer], loading: true, streamingChunks: [] }
            : chat
    );
};

// Helper function to build chat request payload
const buildChatRequest = (
    question: string,
    chat: ChatItem | undefined,
    includeDeviceInfo: boolean = false
) => {
    const personalizationStore = usePersonalizationStore.getState();
    const baseRequest = {
        question,
        chat_id: chat?.chat_id || undefined,
        user_id: useSecuritySetting.getState().authRequired ? "" : persistStore.getState().userId,
        start_new_chat: chat?.answers?.length ? chat.startNewChat || false : false,
        answer_length: personalizationStore.answerLength,
        enable_icons: personalizationStore.enableIcons,
        response_tone: personalizationStore.responseTone,
    };

    if (includeDeviceInfo) {
        return {
            ...baseRequest,
            speech_ids: useSpeechStore.getState().instanceIds,
            device_info: navigator.userAgent,
        };
    }

    return baseRequest;
};

// Helper function to update chat with final answer
const updateChatWithAnswer = (
    currentChats: ChatItem[],
    question: string,
    answer: string,
    chatId: string,
    rowKey: string,
    sources?: any[],
    startNewChat: boolean = false,
    nextPromptSuggestions: string[] = []
): ChatItem[] => {
    return currentChats.map((chat) => {
        if (chat.active) {
            return {
                ...chat,
                answers: chat.answers.map((answerPair, idx) => {
                    const isLastAnswer = idx === chat.answers.length - 1;
                    if (isLastAnswer && answerPair[0] === question) {
                        return [
                            question,
                            {
                                answer,
                                chat_id: chatId,
                                row_key: rowKey,
                                start_new_chat: startNewChat,
                                sources: sources || [],
                                next_prompt_suggestions: nextPromptSuggestions,
                            }
                        ] as [string, ChatV2response];
                    }
                    return answerPair;
                }),
                chat_id: chatId,
                moveTo: rowKey,
                loading: false,
                active: true,
                error: "",
                startNewChat: startNewChat,
                updateMessage: undefined,
            };
        }
        return chat;
    });
};

// Helper function to add item to chat history
const addToHistory = (question: string, chatId: string, rowKey: string) => {
    useHistoryStore.getState().addItem({
        RowKey: rowKey,
        chat_id: chatId,
        end: "",
        question,
        status: "active",
        count: 0
    });
};

// Helper function to handle streaming response
const handleStreamingResponse = async (
    stream: AsyncGenerator<any>,
    question: string,
    set: (state: any) => void,
    get: () => ChatStore
) => {
    let accumulatedAnswer = "";
    let lastResponseText = "";
    let finalChatId = "";
    let sources: any[] = [];
    let rowKey = "";

    for await (const packet of stream) {
        if (packet.event === "Chunk") {
            const chunkData = packet.data as { type: string; text: string; index: number };

            window.dispatchEvent(new CustomEvent('streaming-chunk', {
                detail: { question, content: JSON.stringify(chunkData), chatId: finalChatId }
            }));

            if (chunkData.type !== "Response") {
                set({
                    chats: get().chats.map((chat) =>
                        chat.active
                            ? {
                                ...chat,
                                streamingChunks: [
                                    ...(chat.streamingChunks || []),
                                    { type: chunkData.type, text: chunkData.text }
                                ],
                                ...(chunkData.type === "NodeUpdate" ? { updateMessage: chunkData.text } : {}),
                            }
                            : chat
                    ),
                });
            }
            if (chunkData.type === "Response") {
                const incoming = typeof chunkData.text === "string" ? chunkData.text : "";
                if (incoming) {
                    const latestChange = incoming.startsWith(lastResponseText)
                        ? incoming.slice(lastResponseText.length)
                        : incoming;

                    if (incoming.startsWith(lastResponseText)) {
                        lastResponseText = incoming;
                    } else {
                        lastResponseText += latestChange;
                    }

                    accumulatedAnswer += latestChange;

                    set({
                        chats: get().chats.map((chat) => {
                            if (!chat.active) {
                                return chat;
                            }

                            return {
                                ...chat,
                                answers: chat.answers.map((answerPair, idx) => {
                                    const isLastAnswer = idx === chat.answers.length - 1;
                                    if (isLastAnswer && answerPair[0] === question) {
                                        return [
                                            question,
                                            {
                                                ...answerPair[1],
                                                answer: accumulatedAnswer,
                                            },
                                        ] as [string, ChatV2response];
                                    }
                                    return answerPair;
                                }),
                            };
                        }),
                    });
                }
            } else if (chunkData.type === "LiveAgentMessage") {
                // Live agent sent a message — append to escalation messages
                const newMessage: EscalationMessage = {
                    sender: "agent",
                    text: chunkData.text,
                    timestamp: new Date().toISOString(),
                };
                set({
                    chats: get().chats.map((chat) =>
                        chat.active ? {
                            ...chat,
                            escalationMessages: [...(chat.escalationMessages || []), newMessage],
                        } : chat
                    )
                });
            } else if (chunkData.type === "SessionEnd") {
                // Live agent session ended
                const endMessage: EscalationMessage = {
                    sender: "agent",
                    text: chunkData.text,
                    timestamp: new Date().toISOString(),
                };
                set({
                    chats: get().chats.map((chat) =>
                        chat.active ? {
                            ...chat,
                            escalationMessages: [...(chat.escalationMessages || []), endMessage],
                            escalationActive: false,
                            escalationEnded: true,
                        } : chat
                    )
                });
            } else if (chunkData.type === "Keepalive") {
                // Ignore keepalive pings
            }
        } else if (packet.event === "EscalationStarted") {
            // Escalation confirmed — switch to live agent mode
            const doneData = packet.data as { chatId: string; sources: any[]; answer?: string; escalation_active?: boolean; escalation_request_id?: string };
            finalChatId = doneData.chatId;
            sources = doneData.sources || [];
            if (doneData.answer) {
                accumulatedAnswer = doneData.answer;
            }
            set({
                chats: get().chats.map((chat) =>
                    chat.active ? {
                        ...chat,
                        escalationActive: true,
                        escalationRequestId: doneData.escalation_request_id || "",
                        escalationMessages: [],
                        loading: false,
                        updateMessage: undefined,
                    } : chat
                )
            });
        } else if (packet.event === "Done") {
            const doneData = packet.data as { chatId: string; sources: any[]; answer?: string; rowId?: string };
            finalChatId = doneData.chatId;
            sources = doneData.sources || [];
            rowKey = doneData.rowId || "";
            // Prefer the fully consolidated answer from Done if provided
            if (doneData.answer) {
                accumulatedAnswer = doneData.answer;
            }
        } else if (packet.event === "Error") {
            const errorData = packet.data as { type: string; status: number; description: string };
            console.error("Streaming error:", errorData);
            throw new Error(errorData.description || "Streaming error occurred");
        }
    }

    await useSpeechStore.getState().resetIDs();

    return { answer: accumulatedAnswer, chatId: finalChatId, rowKey, sources };
};


export const useChatStore = create<ChatStore>((set, get) => ({
    citation: "",
    chats: [],
    loadingHistory: false,
    streamedData: null,
    isStreaming: false,
    streamingError: null,
    runStreamingChat: async () => {
        set({ streamedData: null, streamingError: null, isStreaming: true });

        try {
            const stream = chatApiStream({
                question: "Streaming test",
                user_id: useSecuritySetting.getState().authRequired
                    ? ""
                    : persistStore.getState().userId,
                start_new_chat: false,
            });

            let lastResponseText = "";

            for await (const packet of stream) {
                if (packet.event === "Chunk") {
                    const chunkData = packet.data as { type?: string; text?: string };
                    if (chunkData.type === "Response" && typeof chunkData.text === "string") {
                        const latestChange = chunkData.text.startsWith(lastResponseText)
                            ? chunkData.text.slice(lastResponseText.length)
                            : chunkData.text;

                        if (chunkData.text.startsWith(lastResponseText)) {
                            lastResponseText = chunkData.text;
                        } else {
                            lastResponseText += latestChange;
                        }

                        set((state: ChatStore) => ({
                            streamedData: [
                                ...(Array.isArray(state.streamedData) ? state.streamedData : []),
                                latestChange,
                            ],
                        }));
                    } else {
                        set((state: ChatStore) => ({
                            streamedData: [
                                ...(Array.isArray(state.streamedData) ? state.streamedData : []),
                                JSON.stringify({ event: packet.event, data: packet.data }, null, 2),
                            ],
                        }));
                    }
                } else if (packet.event === "Done") {
                    // Keep latest streamed chunk in state; final answer rendering is handled separately.
                } else {
                    set((state: ChatStore) => ({
                        streamedData: [
                            ...(Array.isArray(state.streamedData) ? state.streamedData : []),
                            JSON.stringify({ event: packet.event, data: packet.data }, null, 2),
                        ],
                    }));
                }
            }
        } catch (e: unknown) {
            if (e instanceof Error) {
                set({ streamingError: e.message });
            } else {
                set({ streamingError: "Stream failed unexpectedly." });
            }
        } finally {
            set({ isStreaming: false });
        }
    },
    loadChat: async (chat_id: string, moveTo: string) => {
        get().onReset();
        set({ loadingHistory: true });
        const updateChatState = (chat_id: string, parsedAnswers: [string, ChatV2response][], moveTo: string, loading: boolean) => {
            set({
                chats: get().chats.map((chat: ChatItem) => chat.chat_id === chat_id ? {
                    chat_id: chat_id,
                    loading: loading,
                    answers: parsedAnswers,
                    active: true,
                    moveTo: moveTo,
                    error: "",
                    startNewChat: false,
                    updateMessage: undefined,
                    loadedFromHistory: chat.loadedFromHistory,
                } : {
                    ...chat,
                    active: false
                })
            });
        };

        if (!get().chats.find((chat: ChatItem) => chat.chat_id === chat_id)) {
            set({
                chats: [
                    ...get().chats,
                    {
                        chat_id: chat_id,
                        loading: true,
                        answers: [["", { answer: "", chat_id: "", row_key: "", start_new_chat: false }]],
                        active: true,
                        moveTo: "",
                        error: "",
                        startNewChat: false,
                        updateMessage: undefined,
                        loadedFromHistory: true,
                    }
                ]
            });

            const result = await getChat(chat_id);
            const parsedAnswers: [string, ChatV2response][] = result.map(record => {
                const response: ChatV2response = {
                    answer: record.answer ?? "",
                    chat_id: chat_id,
                    sources: record.sources,
                    uncited_sources: record.uncited_sources,
                    row_key: record.row_key,
                    start_new_chat: false,
                    feedback: record.feedback ? record.feedback : { rating: 0, text: "" } as FeedbackV2,
                };
                return [record.question, response];
            });
            updateChatState(chat_id, parsedAnswers, moveTo, false);
        } else {
            const existingChat = get().chats.find((chat: ChatItem) => chat.chat_id === chat_id);
            if (existingChat) {
                existingChat.loadedFromHistory = true;
            }
            updateChatState(chat_id, existingChat?.answers || [], moveTo, false);
        }
        set({ loadingHistory: false });
    },
    updateFeedback: async (row_key: string, answer: string, rating: number, message: string, chat_id: string) => {
        let feedbackToSend: FeedbackV2 | undefined;

        set({
            chats: get().chats.map((chat: ChatItem) => {
                if (chat.chat_id !== chat_id) {
                    return chat;
                }

                return {
                    ...chat,
                    answers: chat.answers.map((_answer: [string, ChatV2response]) => {
                        if (_answer[1].row_key !== row_key) {
                            return _answer;
                        }

                        const feedback = { rating, answer, row_key, message };
                        feedbackToSend = feedback;

                        return [_answer[0], { ..._answer[1], feedback }];
                    })
                };
            })
        });

        if (feedbackToSend && feedbackToSend.rating > 0) {
            await sendFeedbackApi(feedbackToSend);
        }
    },
    chatMessage: async (question: string, _speech_ids?: string[], _reset?: boolean) => {
        const streamingMode = useConfigStore.getState().streamingEnabled;
        const hasActiveChat = get().currentChat() !== undefined;

        if (streamingMode) {
            set({ isStreaming: true, streamingError: null, streamedData: null });
        }

        // Prepare initial state
        set({ chats: prepareInitialChatState(question, get().chats, hasActiveChat) });

        const chat = get().currentChat();

        try {
            if (streamingMode) {
                // Streaming mode
                const stream = chatApiStream(buildChatRequest(question, chat, true));
                const result = await handleStreamingResponse(
                    stream,
                    question,
                    set,
                    get
                );

                set({
                    chats: updateChatWithAnswer(
                        get().chats,
                        question,
                        result.answer,
                        result.chatId,
                        result.rowKey,
                        result.sources
                    )
                });

                addToHistory(question, result.chatId, result.rowKey);
            } else {
                // Non-streaming mode
                const result = await chatApi({
                    ...buildChatRequest(question, chat, true),
                    start_new_chat: _reset ? _reset : false,
                });
                set({
                    chats: updateChatWithAnswer(
                        get().chats,
                        question,
                        result.answer,
                        result.chat_id,
                        result.row_key,
                        result.sources,
                        result.start_new_chat,
                        result.next_prompt_suggestions
                    )
                });
                addToHistory(question, result.chat_id || "", result.row_key || "");
            }
            useSpeechStore.getState().resetIDs();
        } catch (e: unknown) {
            if (e instanceof Error) {
                set({
                    chats: get().chats.map((_chat: ChatItem) => _chat.chat_id === chat?.chat_id ? {
                        ..._chat,
                        loading: false,
                        error: String(e)
                    } : _chat)
                });
                if (streamingMode) {
                    set({ streamingError: e.message });
                }
                useMessageStore.getState().addError(e.message);
            }
        } finally {
            if (streamingMode) {
                set({ isStreaming: false });
            }
        }
    },
    sendEscalationMessage: async (message: string) => {
        const chat = get().currentChat();
        if (!chat?.escalationActive || !chat?.escalationRequestId) return;

        // Add customer message to the escalation messages immediately
        const customerMessage: EscalationMessage = {
            sender: "customer",
            text: message,
            timestamp: new Date().toISOString(),
        };
        set({
            chats: get().chats.map((_chat: ChatItem) =>
                _chat.active ? {
                    ..._chat,
                    escalationMessages: [...(_chat.escalationMessages || []), customerMessage],
                } : _chat
            )
        });

        // Send to backend → Medley
        try {
            await sendEscalationMessageApi(chat.escalationRequestId, message);
        } catch (e) {
            console.error("Failed to send escalation message:", e);
            if (e instanceof ApiError && e.status === 404) {
                set({
                    chats: get().chats.map((_chat: ChatItem) =>
                        _chat.active ? {
                            ..._chat,
                            escalationActive: false,
                            escalationEnded: true,
                        } : _chat
                    )
                });
                useMessageStore.getState().addError("Live agent session has ended");
            } else {
                useMessageStore.getState().addError("Failed to send message to agent");
            }
        }
    },
    onReset: () => {
        set({ chats: get().chats.map((chat: ChatItem) => ({ ...chat, active: false, startNewChat: false, loading: false })) });
    },
    currentChat: () => {
        return get().chats.find((chat: ChatItem) => chat.active === true) || undefined;
    },
    setCitation: (citation: string) => {
        set({ citation });
    },
    getActiveChat: () => {
        const chats = get().chats;
        return Array.isArray(
            chats.filter(chat => chat.active)
        ) && chats.filter(
            chat => chat.active
        ).length > 0 ? chats.filter(chat => chat.active)[0] : {
            answers: [],
            loading: false,
            active: false,
            moveTo: "",
            chat_id: "",
            error: "",
            startNewChat: false,
            row_key: "",
            updateMessage: undefined
        } as ChatItem;
    },
    continueChat: (chat_id: string) => {
        const chat = get().getActiveChat();
        const message = chat.answers.length > 1 ? chat.answers[chat.answers.length - 1][0] : "";
        get().chatMessage(message);
        set({
            chats: get().chats.map((chat: ChatItem) => chat.chat_id === chat_id ? {
                ...chat, startNewChat: false, answers: chat.answers.slice(0, -1)

            } : { ...chat })
        });
    },
    onRetry: async (message: string) => {
        const chat = get().currentChat();
        if (!chat) {
            return;
        }

        set({
            chats: get().chats.map((_chat: ChatItem) => {
                if (_chat !== chat) {
                    return _chat;
                }

                const isLastAnswerRetryTarget = _chat.answers[_chat.answers.length - 1]?.[0] === message;

                return {
                    ..._chat,
                    answers: isLastAnswerRetryTarget ? _chat.answers.slice(0, -1) : _chat.answers,
                    error: "",
                    loading: false,
                };
            })
        });

        await get().chatMessage(message);
    }
}));
